import { MenuOutlined } from "@ant-design/icons";
import { Dropdown, Menu, Select } from "antd";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import Data360LiveIWMGraph from "./Data360LiveIWMGraph";
import { tlogGraphHelperData } from "../../helpers";
// import { tlogMapData } from "../../helpers/tlog/tlog-data-mapper";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { tlogGraphHelper } from "../../helpers/tlog/tlog-graph";
import {
  tlogHelper,
  applyConversionFactor,
} from "../../helpers/tlog/tlog-helper";
import { GlobalUnitEnum } from "../../helpers/unit-conversion";
import { FETCH_TLOG_DATA_SAGA } from "../../redux/data360LiveTlog/types";
import { getLimits } from "../../redux/data360Live_StatusConfiguration/action";
import CommonLoader from "../Loaders/loader";

const { Option } = Select;

const TlogGraphView = ({
  s_deviceid_event,
  view,
  torch,
  // zone,
  tilt_start,
  tilt_end,
  rawZone,
}) => {
  const dispatch = useDispatch();
  const tlogState = useSelector((state) => state.data360LiveTlogReducer);
  const currentProject = useSelector(
    (state) => state.data360LiveProjectReducer?.currentProject || {}
  );
  const themeState = useSelector(
    (state) => state.switchThemeReducer?.switchTheme
  );

  // Get status configuration data
  const statusConfigState = useSelector(
    (state) => state.data360LiveStatusConfigReducer
  );
  const statusTableLimitData = statusConfigState?.limits?.allDataSet || [];

  const loader = tlogState?.isLoading;
  const tlogData = tlogState?.data?.data || [];
  const tableLimitData = tlogState?.data?.tableLimit || [];
  const reportColumns =
    logDataHelperFunctions.data360LiveGetReportColumns(currentProject);

  // Use status configuration data if available, otherwise fall back to table limit data
  const effectiveTableLimitData =
    statusTableLimitData.length > 0 ? statusTableLimitData : tableLimitData;

  const [selectedOption, setSelectedOption] = useState("Chart 1");
  const [selectedOption2, setSelectedOption2] = useState("Chart 2");
  const [selectedOption3, setSelectedOption3] = useState("Chart 3");
  const [selectedOption4, setSelectedOption4] = useState("Chart 4");
  const [selectedOption5, setSelectedOption5] = useState("Chart 5");
  const [selectedOption6, setSelectedOption6] = useState("Chart 6");

  const colorOptions = [
    {
      value: "Travel Speed (in/min)",
      label: "Travel Speed (in/min)",
      key: "travelSpeed",
      color: "#e03540",
    },
    {
      value: "Volts (v)",
      key: "volt",
      label: "Volts (v)",
      color: "#FFA500",
    },
    {
      value: "Current (A)",
      key: "current",
      label: "Current (A)",
      color: "#ee82ee",
    },
    {
      value: "Wire Speed (in/min)",
      label: "Wire Speed (in/min)",
      key: "wireSpeed",
      color: "#800080",
    },
    {
      value: "Oscillate Width (in)",
      label: "Oscillate Width (in)",
      key: "oscillationWidth",
      color: "#A52A2A",
    },
    {
      value: "Target (A)",
      label: "Target (A)",
      key: "target",
      color: "#808080",
    },
    {
      value: "Horizontal Bias (A)",
      label: "Horizontal Bias (A)",
      key: "horizontalBias",
      color: "#00FFFF",
    },
    {
      value: "Frequency (cyc/min)",
      label: "Frequency (cyc/min)",
      key: "frequency",
      color: "#FFC0CB",
    },
    {
      value: "Total Wire Consumed (in)",
      label: "Total Wire Consumed (in)",
      key: "totalWireConsumed",
      color: "#FFFF00",
    },
    {
      value: "True Energy (KJ/in)",
      label: "True Energy (KJ/in)",
      key: "trueEnergy",
      color: "#0000FF",
    },
    {
      value: "Heat (kJ/in)",
      label: "Heat (kJ/in)",
      key: "heat",
      color: "#008000",
    },
    {
      value: "Arc Impedance (ohms)",
      label: "Arc Impedance (ohms)",
      key: "arcImpedance",
      color: "#FF6347",
    },
  ];

  useEffect(() => {
    if (currentProject.project_id && currentProject.project_name) {
      const payload = {
        project_id: currentProject.project_id,
        project_name: currentProject.project_name,
        welduid_s_event_torch: s_deviceid_event,
        view_type: view,
        torch_type: torch,
        ...(rawZone && { zone_name: rawZone }),
        ...(tilt_start == 0 || tilt_start ? { tilt_start: tilt_start } : {}),
        ...(tilt_end && { tilt_end: tilt_end }),
      };

      dispatch({
        type: FETCH_TLOG_DATA_SAGA,
        payload: payload,
      });
    }
  }, [
    dispatch,
    s_deviceid_event,
    currentProject,
    view,
    torch,
    rawZone,
    tilt_start,
    tilt_end,
  ]);

  // Fetch status configuration data
  useEffect(() => {
    if (currentProject.project_id) {
      const params = {
        project_number: currentProject.project_id,
      };
      dispatch(getLimits(params));
    }
  }, [dispatch, currentProject.project_id]);

  const cleanData = (dataArray, factor) =>
    dataArray
      .filter((value) => value !== "" && !isNaN(parseFloat(value)))
      .map((value) => parseFloat(applyConversionFactor(value, factor)));

  const categories = useMemo(
    () => cleanData(tlogData?.map((item) => item.tilt)),
    [tlogData]
  );

  const conversionFactors = tlogState?.data?.conversionFactors || {};

  const travelSpeeds = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.travel_speed),
        conversionFactors?.travel_speed
      ),
    [tlogData, conversionFactors]
  );

  const volts = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.volts),
        conversionFactors?.volts
      ),
    [tlogData, conversionFactors]
  );

  const current = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.current),
        conversionFactors?.current
      ),
    [tlogData, conversionFactors]
  );

  const wireSpeed = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.wire_speed),
        conversionFactors?.wire_speed
      ),
    [tlogData, conversionFactors]
  );

  const oscillationWidth = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.oscillation_width),
        conversionFactors?.oscillation_width
      ),
    [tlogData, conversionFactors]
  );

  const target = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.target),
        conversionFactors?.target
      ),
    [tlogData, conversionFactors]
  );

  const horizontalBias = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.horizontal_bias),
        conversionFactors?.horizontal_bias
      ),
    [tlogData, conversionFactors]
  );

  const frequency = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.frequency),
        conversionFactors?.frequency
      ),
    [tlogData, conversionFactors]
  );

  const totalWireConsumed = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.total_wire_consumed),
        conversionFactors?.total_wire_consumed
      ),
    [tlogData, conversionFactors]
  );

  const trueEnergy = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.true_energy),
        conversionFactors?.true_energy
      ),
    [tlogData, conversionFactors]
  );

  const heat = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.heat),
        conversionFactors?.heat
      ),
    [tlogData, conversionFactors]
  );

  const arcImpedance = useMemo(
    () =>
      cleanData(
        tlogData?.map((item) => item.arc_impedance),
        conversionFactors?.arc_impedance
      ),
    [tlogData, conversionFactors]
  );

  const empty = [];

  // Add the same getTableLimitColumnValue function as in tlog_grid.js
  const getTableLimitColumnValue = (column) => {
    switch (column) {
      case "volts":
        return "volts";
      case "current":
        return "current";
      case "wireSpeed":
        return "wire_speed";
      case "trueEnergy":
        return "true_energy";
      case "oscillationWidth":
        return "oscillation_width";
      case "heat":
        return "heat";
      case "travelSpeed":
        return "travel_speed";
      default:
        return column;
    }
  };

  // Use shared getRangeDisplay function from tlogHelper
  const getRangeDisplay = useCallback(
    ({ tlogItem, limitData, reportColumns, column }) => {
      const globalUnit = currentProject?.globalUnit || GlobalUnitEnum.IMPERIAL;
      return tlogHelper.getRangeDisplay({
        tlogItem,
        limitData,
        reportColumns,
        column,
        globalUnit,
      });
    },
    [currentProject]
  );

  // Updated getMinMaxValues function to use the same logic as table components
  const getMinMaxValues = useCallback(
    (column) => {
      const tlogItem = tlogData?.[0];
      if (!tlogItem) {
        return { min: 0, max: 0 };
      }

      // Map the column name to match the table's column mapping
      const mappedColumn = getTableLimitColumnValue(column);

      const rangeResult = getRangeDisplay({
        tlogItem,
        limitData: effectiveTableLimitData,
        reportColumns,
        column: mappedColumn,
      });

      if (!rangeResult.doesExist) {
        return { min: 0, max: 0 };
      }

      // Extract min and max values from the range string
      const rangeMatch = rangeResult.showColor.match(/\[(.*?) - (.*?)\]/);
      if (rangeMatch) {
        const minValue = parseFloat(rangeMatch[1]);
        const maxValue = parseFloat(rangeMatch[2]);
        return {
          min: isNaN(minValue) ? 0 : minValue,
          max: isNaN(maxValue) ? 0 : maxValue,
        };
      }

      return { min: 0, max: 0 };
    },
    [tlogData, effectiveTableLimitData, reportColumns, getRangeDisplay]
  );

  const mappedColumns = useMemo(() => {
    const columns = tlogGraphHelper.mapTlogGraphColumns(
      colorOptions,
      reportColumns
    );

    // Filter out columns with no data
    return columns.filter((column) => {
      switch (column.key) {
        case tlogGraphHelper.columnsMapping.ARC_IMPEDANCE:
          return arcImpedance.length > 0;
        case tlogGraphHelper.columnsMapping.TRAVEL_SPEED:
          return travelSpeeds.length > 0;
        case tlogGraphHelper.columnsMapping.VOLT_V:
          return volts.length > 0;
        case tlogGraphHelper.columnsMapping.CURRENT_A:
          return current.length > 0;
        case tlogGraphHelper.columnsMapping.WIRE_SPEED:
          return wireSpeed.length > 0;
        case tlogGraphHelper.columnsMapping.OSCILLATION_WIDTH:
          return oscillationWidth.length > 0;
        case tlogGraphHelper.columnsMapping.TARGET:
          return target.length > 0;
        case tlogGraphHelper.columnsMapping.HORIZONTAL_BIAS:
          return horizontalBias.length > 0;
        case tlogGraphHelper.columnsMapping.FREQUENCY:
          return frequency.length > 0;
        case tlogGraphHelper.columnsMapping.TOTAL_WIRE_CONSUMED:
          return totalWireConsumed.length > 0;
        case tlogGraphHelper.columnsMapping.TRUE_ENERGY:
          return trueEnergy.length > 0;
        case tlogGraphHelper.columnsMapping.HEAT:
          return heat.length > 0;
        default:
          return true;
      }
    });
  }, [
    reportColumns,
    arcImpedance,
    travelSpeeds,
    volts,
    current,
    wireSpeed,
    oscillationWidth,
    target,
    horizontalBias,
    frequency,
    totalWireConsumed,
    trueEnergy,
    heat,
  ]);

  const processedDataRef = useRef({
    categories: [],
    graphData: {},
  });

  const getGraphData1 = useMemo(
    () => (selectedOption) => {
      let yAxisColor;
      let yAxisTitle;
      let minValue = 0;
      let maxValue = 0;
      let currentData = [];

      switch (selectedOption) {
        case "Travel Speed (in/min)":
          yAxisColor = "#e03540";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.TRAVEL_SPEED
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("travelSpeed"));
          currentData = travelSpeeds;
          break;

        case "Volts (v)":
          yAxisColor = "#FFA500";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.VOLT_V
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("volts"));
          currentData = volts;
          break;

        case "Current (A)":
          yAxisColor = "#ee82ee";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.CURRENT_A
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("current"));
          currentData = current;
          break;

        case "Wire Speed (in/min)":
          yAxisColor = "#800080";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.WIRE_SPEED
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("wireSpeed"));
          currentData = wireSpeed;
          break;

        case "Oscillate Width (in)":
          yAxisColor = "#A52A2A";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.OSCILLATION_WIDTH
          )?.label;
          ({ min: minValue, max: maxValue } =
            getMinMaxValues("oscillationWidth"));
          currentData = oscillationWidth;
          break;

        case "Target (A)":
          yAxisColor = "#808080";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.TARGET
          )?.label;
          currentData = target;
          break;

        case "Horizontal Bias (A)":
          yAxisColor = "#00FFFF";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.HORIZONTAL_BIAS
          )?.label;
          currentData = horizontalBias;
          break;

        case "Frequency (cyc/min)":
          yAxisColor = "#FFC0CB";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.FREQUENCY
          )?.label;
          currentData = frequency;
          break;

        case "Total Wire Consumed (in)":
          yAxisColor = "#FFFF00";
          yAxisTitle = mappedColumns.find(
            (ite) =>
              ite.key == tlogGraphHelper.columnsMapping.TOTAL_WIRE_CONSUMED
          )?.label;
          currentData = totalWireConsumed;
          break;

        case "True Energy (KJ/in)":
          yAxisColor = "#0000FF";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.TRUE_ENERGY
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("trueEnergy"));
          currentData = trueEnergy;
          break;

        case "Heat (kJ/in)":
          yAxisColor = "#008000";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.HEAT
          )?.label;
          ({ min: minValue, max: maxValue } = getMinMaxValues("heat"));
          currentData = heat;
          break;

        case "Arc Impedance (ohms)":
          yAxisColor = "#FF6347";
          yAxisTitle = mappedColumns.find(
            (ite) => ite.key == tlogGraphHelper.columnsMapping.ARC_IMPEDANCE
          )?.label;
          currentData = arcImpedance;
          break;

        default:
          yAxisColor = "#0000FF";
          yAxisTitle = "Chart 1";
          currentData = empty;
          break;
      }

      processedDataRef.current.categories = categories;
      processedDataRef.current.graphData[selectedOption] = {
        data: currentData,
        minValue,
        maxValue,
      };

      return tlogGraphHelperData.GraphOne(
        themeState,
        currentData,
        categories,
        yAxisTitle,
        yAxisColor,
        minValue,
        maxValue,
        currentProject.project_number,
        currentProject.project_name
      );
    },
    [
      mappedColumns,
      travelSpeeds,
      volts,
      current,
      wireSpeed,
      oscillationWidth,
      target,
      horizontalBias,
      frequency,
      totalWireConsumed,
      trueEnergy,
      heat,
      categories,
      themeState,
      currentProject,
      getMinMaxValues,
      arcImpedance,
    ]
  );

  const FirstGraph = useMemo(
    () => getGraphData1(selectedOption),
    [getGraphData1, selectedOption]
  );
  const SecondGraph = useMemo(
    () => getGraphData1(selectedOption2),
    [getGraphData1, selectedOption2]
  );
  const ThirdGraph = useMemo(
    () => getGraphData1(selectedOption3),
    [getGraphData1, selectedOption3]
  );
  const FourthGraph = useMemo(
    () => getGraphData1(selectedOption4),
    [getGraphData1, selectedOption4]
  );
  const FifthGraph = useMemo(
    () => getGraphData1(selectedOption5),
    [getGraphData1, selectedOption5]
  );
  const SixthGraph = useMemo(
    () => getGraphData1(selectedOption6),
    [getGraphData1, selectedOption6]
  );

  const isDataEmpty = FirstGraph.series[0].data.length === 0;
  const isDataEmpty2 = SecondGraph.series[0].data.length === 0;
  const isDataEmpty3 = ThirdGraph.series[0].data.length === 0;
  const isDataEmpty4 = FourthGraph.series[0].data.length === 0;
  const isDataEmpty5 = FifthGraph.series[0].data.length === 0;
  const isDataEmpty6 = SixthGraph.series[0].data.length === 0;

  const downloadChartCSV = (graphData, selectedOption) => {
    const storedData = processedDataRef.current;
    const categories = storedData.categories;
    const currentGraphData = storedData.graphData[selectedOption];

    if (!categories.length || !currentGraphData) {
      console.error("No data available for download");
      return;
    }

    const mainData = currentGraphData.data;
    const minData =
      graphData.series.find((series) => series.name === "Min")?.data || [];
    const maxData =
      graphData.series.find((series) => series.name === "Max")?.data || [];

    const selectedOptionLabel = mappedColumns.find(
      (item) => item.value === selectedOption
    )?.label;

    let headers = ["Tlog", selectedOptionLabel];

    if (minData.length > 0 && minData[0] > 0) {
      headers.push("Min Limit");
    }
    if (maxData.length > 0 && maxData[0] > 0) {
      headers.push("Max Limit");
    }

    const rows = categories.map((category, index) => {
      let row = [category, mainData[index]];

      if (minData.length > 0 && minData[0] > 0) {
        row.push(minData[index]);
      }
      if (maxData.length > 0 && maxData[0] > 0) {
        row.push(maxData[index]);
      }

      return row;
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${currentProject.project_number}_${currentProject.project_name}_${selectedOptionLabel}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getChartMenu = (graphData, selectedOption) => (
    <Menu>
      <Menu.Item onClick={() => downloadChartCSV(graphData, selectedOption)}>
        Download CSV
      </Menu.Item>
    </Menu>
  );

  if (!categories.length) {
    return (
      <Data360LiveIWMGraph
        tlogData={tlogData}
        loader={loader}
        reportColumns={reportColumns}
        tableLimitData={tableLimitData}
        torch={torch}
        conversionFactors={conversionFactors}
      />
    );
  }

  return (
    <div className="tlog-graph">
      {loader ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <>
          <div className="grapview-selector">
            <Select
              value={selectedOption}
              onChange={(value) => setSelectedOption(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
            <Select
              value={selectedOption2}
              onChange={(value) => setSelectedOption2(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
            <Select
              value={selectedOption3}
              onChange={(value) => setSelectedOption3(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
            <Select
              value={selectedOption4}
              onChange={(value) => setSelectedOption4(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
            <Select
              value={selectedOption5}
              onChange={(value) => setSelectedOption5(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
            <Select
              value={selectedOption6}
              onChange={(value) => setSelectedOption6(value)}
              className="graph-select"
              getPopupContainer={(trigger) => trigger.parentNode}
              dropdownStyle={{ minWidth: 200 }}
            >
              {mappedColumns.map((option) => (
                <Option key={option.value} value={option.value}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: "10px",
                        height: "10px",
                        backgroundColor: option.color,
                        marginRight: "8px",
                      }}
                    />
                    <span style={{ textTransform: "math-auto" }}>
                      {option.label}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
          <div className="line-graph">
            {!isDataEmpty && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(FirstGraph, selectedOption)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={FirstGraph.options}
                  series={FirstGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
            {!isDataEmpty2 && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(SecondGraph, selectedOption2)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={SecondGraph.options}
                  series={SecondGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
            {!isDataEmpty3 && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(ThirdGraph, selectedOption3)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={ThirdGraph.options}
                  series={ThirdGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
            {!isDataEmpty4 && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(FourthGraph, selectedOption4)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={FourthGraph.options}
                  series={FourthGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
            {!isDataEmpty5 && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(FifthGraph, selectedOption5)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={FifthGraph.options}
                  series={FifthGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
            {!isDataEmpty6 && (
              <>
                <div className="menuOutlined">
                  <Dropdown
                    overlay={getChartMenu(SixthGraph, selectedOption6)}
                    trigger={["click"]}
                    placement="bottomRight"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    <MenuOutlined style={{ fontSize: "16px" }} />
                  </Dropdown>
                </div>
                <Chart
                  options={SixthGraph.options}
                  series={SixthGraph.series}
                  type="line"
                  height={250}
                  className="Graph-background"
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TlogGraphView;
